import { DBricksConnection } from '../workflow/connections/databricks'
import {
  WorkflowComponentCodeParams,
  Provider,
  TableSchema,
  QueryAndOutputs,
  WorkflowCodeType,
  WorkflowComponentParamType,
  WorkflowComponentParameterMode,
  ColumnDataType,
  Language,
  WorkflowComponentOutputTablesParams
} from '../types'
import { WorkflowComponent } from './common/workflowcomponent'
import { dedent } from 'ts-dedent'
import { SINGLE_OUTPUT_NAME } from './common/singleoutputcomponent'

const expressionPlaceholders: Record<string, string> = {
  [Provider.BigQuery]: `{
    "mode_value": {
     "formula": "APPROX_TOP_COUNT(value, 1)[OFFSET(0)].value",
     "type": "String"
    },
    "conditional_count": {
     "formula": "COUNTIF(column = 'value') > 0",
     "type": "Boolean"
    }
  }`,
  [Provider.Snowflake]: `{
    "POPULATION_SUM": {
      "formula": "SUM(POPULATION)",
      "type": "Number"
    },
    "CITY_NAME": {
      "formula": "IFF(COUNT(*) <= 1, ANY_VALUE(CITY_NAME), NULL)",
      "type": "String"
    }
  }`,
  [Provider.Redshift]: `{
    "population_sum": {
      "formula": "SUM(population)",
      "type": "Number"
    },
    "city_name": {
      "formula": "(CASE WHEN COUNT(*) <= 1 THEN ANY_VALUE(city_name) ELSE NULL END)",
      "type": "String"
    }
  }`,
  [Provider.Postgres]: `{
    "population_sum": {
      "formula": "SUM(population)",
      "type": "Number"
    },
    "city_name": {
      "formula": "(CASE WHEN COUNT(*) <= 1 THEN ANY_VALUE(city_name) ELSE NULL END)",
      "type": "String"
    }
  }`,
  [Provider.DatabricksRest]:
    'SUM(population) AS population_sum; (CASE WHEN COUNT(*) <= 1 THEN ANY_VALUE(city_name) ELSE NULL END) AS city_name'
}

const methods: Record<string, Map<string, string>> = {
  [Provider.BigQuery]: new Map([
    ['max', 'MAX($1)'],
    ['min', 'MIN($1)'],
    ['sum', 'SUM($1)'],
    ['count', 'COUNT($1)'],
    ['concat', 'STRING_AGG($1)'],
    ['avg', 'AVG($1)'],
    ['any', 'ANY_VALUE($1)'],
    ['array', 'ARRAY_AGG($1)']
  ]),
  [Provider.Snowflake]: new Map([
    ['max', 'MAX($1)'],
    ['min', 'MIN($1)'],
    ['sum', 'SUM($1)'],
    ['count', 'COUNT($1)'],
    ['concat', "LISTAGG($1, ',')"],
    ['avg', 'AVG($1)'],
    ['any', 'ANY_VALUE($1)'],
    ['array', 'ARRAY_AGG($1)']
  ]),
  [Provider.Postgres]: new Map([
    ['max', 'MAX($1)'],
    ['min', 'MIN($1)'],
    ['sum', 'SUM($1)'],
    ['count', 'COUNT($1)'],
    ['concat', "STRING_AGG($1, ',')"],
    ['avg', 'AVG($1)'],
    ['any', 'COALESCE($1)'],
    ['array', 'ARRAY_AGG($1)']
  ]),
  [Provider.Redshift]: new Map([
    ['max', 'MAX($1)'],
    ['min', 'MIN($1)'],
    ['sum', 'SUM($1)'],
    ['count', 'COUNT($1)'],
    ['concat', "LISTAGG(CAST($1 AS TEXT), ',')"],
    ['avg', 'AVG($1)'],
    ['any', 'ANY_VALUE($1)'],
    ['array', `JSON_PARSE('"[' || listagg($1, ',') || ']"')`]
  ]),
  [Provider.DatabricksRest]: new Map([
    ['max', 'MAX($1)'],
    ['min', 'MIN($1)'],
    ['sum', 'SUM($1)'],
    ['count', 'COUNT($1)'],
    ['concat', `CONCAT_WS(',', COLLECT_LIST($1))`],
    ['avg', 'AVG($1)'],
    ['any', 'ANY_VALUE($1)'],
    ['array', `ARRAY_AGG($1)`]
  ])
}

class CreateH3AggTileset extends WorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.createh3aggtileset',
        group: 'Tileset Creation',
        title: 'Create H3 Agg Tileset',
        description: `
          This component creates an aggregation tileset from an input containing H3 cell indexes.
        `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Source table'
        },
        {
          name: 'destination',
          type: WorkflowComponentParamType.OutputTable,
          title: 'Table details',
          placeholder: 'Rename and select destination'
        },
        {
          name: 'h3_column',
          type: WorkflowComponentParamType.Column,
          title: 'H3 column',
          parent: 'source',
          default: 'h3',
          dataType: [ColumnDataType.Number, ColumnDataType.String]
        },
        {
          name: 'h3_resolution',
          type: WorkflowComponentParamType.Number,
          title: 'H3 input data resolution',
          min: 0,
          max: 15,
          default: 0,
          allowExpressions: false,
          helper: 'workflows.parameterForm.h3ResolutionHelper'
        },
        {
          name: 'resolution_min',
          type: WorkflowComponentParamType.Number,
          title: 'Minimum H3 tileset resolution',
          min: 0,
          max: 15,
          mode: WorkflowComponentParameterMode.Slider,
          default: 0,
          allowExpressions: false,
          helper: 'workflows.parameterForm.h3ResolutionMinHelper'
        },
        {
          name: 'resolution_max',
          type: WorkflowComponentParamType.Number,
          title: 'Maximum H3 tileset resolution',
          min: 0,
          max: 15,
          mode: WorkflowComponentParameterMode.Slider,
          default: 6,
          allowExpressions: false,
          helper: 'workflows.parameterForm.h3ResolutionMaxHelper'
        },
        {
          name: 'aggregation_resolution',
          type: WorkflowComponentParamType.Number,
          title: 'Aggregation resolution',
          min: 1,
          max: 8,
          mode: WorkflowComponentParameterMode.Slider,
          default: 4,
          allowExpressions: false,
          helper: 'workflows.parameterForm.h3AggregationResolutionHelper'
        },
        {
          name: 'properties',
          type: WorkflowComponentParamType.SelectColumnAggregation,
          allowExpression: false,
          title: 'Properties',
          placeholder: 'Select fields for aggregation',
          parent: 'source',
          allowExpressions: false,
          noDefault: true
        },
        {
          name: 'addcustomexpression',
          type: WorkflowComponentParamType.Boolean,
          title: 'Add custom expression',
          optional: true,
          default: false
        },
        // Custom expression inputs
        {
          name: 'expression',
          type: WorkflowComponentParamType.Json,
          title: '',
          optional: true,
          default: '',
          placeholder: expressionPlaceholders,
          showIf: [{ parameter: 'addcustomexpression', value: true }]
        },
        {
          name: 'metadata',
          type: WorkflowComponentParamType.Json,
          title: 'Metadata',
          placeholder: 'E.g.: { "name": "...", "description": "..." }',
          default: '',
          allowExpressions: false,
          optional: true,
          advanced: true
        }
      ],
      []
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    envVars,
    componentName,
    codeType,
    schemas
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs | null> {
    const preparedParams = this._prepareParameters(params)

    const propertiesTuples: string[] = (
      preparedParams.properties as string
    )?.split(',')
    let properties = connection.type === Provider.DatabricksRest ? '' : {}
    if (connection.type === Provider.DatabricksRest) {
      /* Takes the chosen properties/aggregation function pairs (eg. ["prop_a", "AVG", "prop_b", "SUM"]
       * and generates a valid property SQL with 'type' and 'formula' fields. Eg:
       *
       *     AVG(population) AS prop_a; SUM(age) AS prop_b
       *
       * */
      properties = propertiesTuples
        ?.reduce((acc: string[], key: string, idx: number) => {
          if (idx % 2 === 0 && propertiesTuples[idx + 1]) {
            // Check for identifier and aggregation function
            const formula =
              methods[connection.type]
                .get(propertiesTuples[idx + 1])
                ?.replace(/\$1/g, key) || ''
            acc.push(`${formula} AS ${key}`)
          }
          return acc
        }, [])
        .join('; ')

      // Handle custom expressions
      if (preparedParams.addcustomexpression) {
        const expressionProps = (preparedParams.expression as string) || ''
        const separator = properties ? '; ' : ''
        properties += separator + expressionProps // Spread within an explicitly new object
      }
    } else {
      /* Takes the chosen properties/aggregation function pairs (eg. ["prop_a", "AVG", "prop_b", "SUM"]
       * and generates a valid property object with 'type' and 'formula' fields. Eg:
       *
       *     {
       *         prop_a: {
       *            "type": "Number",
       *            "formula": "AVG(population)"
       *         },
       *         prop_b: {
       *            "type": "Number",
       *            "formula": "SUM(age)"
       *         },
       *     }
       *
       * */
      properties = propertiesTuples?.reduce((acc, key, idx) => {
        if (idx % 2 === 0) {
          const schema = schemas?.input.source as TableSchema
          let type = schema?.find((item) => item.name === key)?.type as string
          type = type ? type.charAt(0).toUpperCase() + type.slice(1) : type
          const formula =
            methods[connection.type]
              .get(propertiesTuples[idx + 1])
              ?.replace(/\$1/g, key) || ''
          acc[key] = { type, formula }
        }
        return acc
      }, {} as Record<string, { type: string | undefined; formula: string }>)
      // Handle custom expressions
      if (preparedParams.addcustomexpression) {
        const expressionProps = JSON.parse(
          (preparedParams.expression as string) || '{}'
        )
        properties = { ...properties, ...expressionProps } // Spread within an explicitly new object
      }
    }

    const options: Record<string, unknown> = {
      resolution_min: preparedParams.resolution_min,
      resolution_max: preparedParams.resolution_max,
      aggregation_resolution: preparedParams.aggregation_resolution,
      properties: properties,
      metadata: preparedParams.metadata
        ? JSON.parse(preparedParams.metadata as string)
        : {}
    }

    switch (connection.type) {
      case Provider.DatabricksRest:
        options['if_exists'] = 'replace'
        options['h3_column'] = preparedParams.h3_column
        options['h3_resolution'] = preparedParams.h3_resolution
        break
      default:
        options['spatial_index_column'] = `h3:${preparedParams.h3_column}`
        options['resolution'] = preparedParams.h3_resolution
        break
    }

    Object.keys(options).forEach(
      (key) =>
        (options[key] == null || options[key] == '') && delete options[key]
    )

    const quotedSource = connection.quoteName(preparedParams.source as string)
    const quotedDestination = connection.quoteName(
      preparedParams.destination as string
    )
    const quotedJsonOptions =
      connection.type === Provider.DatabricksRest
        ? `'${JSON.stringify(options)}'`
        : connection.quoteMultilineString(JSON.stringify(options))

    let query: string
    if (codeType === WorkflowCodeType.DryRun) {
      query = connection.getCreateTempTableQuery(
        preparedParams.destination as string,
        'SELECT NULL AS tile, NULL AS data',
        codeType,
        null
      )
      if (connection.type === Provider.DatabricksRest) {
        query = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(query)
      }
    } else {
      switch (connection.type) {
        case Provider.BigQuery:
        case Provider.Snowflake:
        case Provider.Postgres:
          query = dedent`
          DROP TABLE IF EXISTS ${quotedDestination};
          CALL ${connection.quoteName(
            envVars.analyticsToolboxDataset as string
          )}.CREATE_SPATIAL_INDEX_TILESET(
            '${quotedSource}',
            '${quotedDestination}',
            ${quotedJsonOptions}
          );
        `
          break
        case Provider.Redshift:
          // Redshift needs destination as variable, not hardcoded string
          query = dedent`
            DECLARE rsDestTable TEXT;
            BEGIN
            rsDestTable := '${quotedDestination}';
            EXECUTE 'DROP TABLE IF EXISTS ' || rsDestTable;
          CALL ${connection.quoteName(
            envVars.analyticsToolboxDataset as string
          )}.CREATE_SPATIAL_INDEX_TILESET(
            '${quotedSource}',
            rsDestTable,
            ${quotedJsonOptions}
          );
            END;
          `
          break
        case Provider.DatabricksRest:
          query = `
spark.read.format("com.carto.analytics.toolbox.stored.procedures.ATStoredProcedureDatasource").option(
      "sql",
      """
      CALL_CARTO carto_un.carto.CREATE_H3_AGG_TILESET(

        '${quotedSource}',
        '${quotedDestination}',
        ${quotedJsonOptions}
        );
      """
    ).load().collect();`
          break
        default:
          return null
      }
    }

    // Create a dummy table to be able to retrieve the status of the node.
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      codeType
    )
    let createTable = connection.getCreateTempTableQuery(
      tablename,
      `SELECT 1 AS N`,
      codeType,
      null
    )
    if (connection.type === Provider.DatabricksRest) {
      createTable = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
        createTable
      )
    }
    query += `\n${createTable}`

    return {
      outputs: {
        result: connection.applyCapitalization(
          preparedParams.destination as string
        )
      },
      query
    }
  }

  getOutputTables({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentOutputTablesParams): Record<string, string> {
    const tablename = connection.getOutputTableName(
      workflow.id,
      componentName,
      params,
      SINGLE_OUTPUT_NAME,
      codeType
    )
    return {
      [SINGLE_OUTPUT_NAME]: tablename
    }
  }

  getUIProperties(provider?: Provider): Record<string, unknown> {
    const classifiedMethods: Record<string, string[]> = {
      [ColumnDataType.Number]: provider
        ? Array.from(methods[provider].keys())
        : [],
      [ColumnDataType.String]: provider
        ? Array.from(methods[provider].keys())
        : []
    }
    return { methods: classifiedMethods }
  }

  language(provider: Provider): Language {
    if (provider === Provider.DatabricksRest) {
      return Language.Python
    }
    return Language.SQL
  }

  getVersion(provider: Provider) {
    return '1'
  }

  requiredAnalyticsToolboxVersion(provider: Provider): string | null {
    return '1.0.0'
  }
}

export { CreateH3AggTileset }
